//@ts-nocheck
import {
  applySnapshot,
  cast,
  flow,
  getEnv,
  getSnapshot,
  types,
} from "mobx-state-tree";
import { TutorJobListStore } from ".";
import { getScheduleFactors } from "../../home-tutoring/request-flow/tutor-fns";
import { IExtendedTutorData } from "../types";
import EditTutorInfo from "./editTutorInfo";
import { SearchStore } from "./search";
import SplitRequestStore from "./splitRequest";
import { determineTutorHourlyRate } from "./utils/tutor-fns";

export const RequestParameters = types
  .model("RequestParameters", {
    searchSubject: types.optional(types.string, ""),
    subjectGroup: types.optional(types.string, ""),
    specialNeeds: types.optional(types.string, ""),
    curriculum: types.optional(types.string, ""),
    class: types.optional(types.string, ""),
    lessonDays: types.optional(types.string, ""),
    lessonTime: types.optional(types.string, ""),
    region: types.optional(types.string, ""),
    state: types.optional(types.string, ""),
    vicinity: types.optional(types.string, ""),
    country: types.optional(types.string, ""),
    teacherKind: types.optional(types.string, ""),
  })
  .views((self) => {
    return {
      get initialValues() {
        let {
          searchSubject,
          subjectGroup,
          specialNeeds,
          curriculum,
          class: klass,
          lessonDays,
          lessonTime,
          region,
          state,
          vicinity,
          country,
          teacherKind,
        } = self;
        return {
          searchSubject,
          subjectGroup,
          specialNeeds,
          curriculum,
          class: klass,
          lessonDays,
          lessonTime,
          region,
          state,
          vicinity,
          country,
          teacherKind,
        };
      },
    };
  });

const ExtendModel = types.model("AdditionalAdminSearchFields", {
  maxNoOfTutors: types.optional(types.number, 3),
  withOptions: types.optional(types.array(SplitRequestStore), []),
  requestParameters: types.optional(RequestParameters, {}),
  currentEditTutorId: types.maybeNull(types.string),
  editTutorInfo: types.optional(EditTutorInfo, {}),
  foundEmail: types.optional(types.string, ""),
  loading: types.optional(types.boolean, false),
});

const NewSearchStore = SearchStore.named("NewSearchStore")
  .views((self) => {
    return {
      get currentEditTutor() {
        return self.oldSearchContextProps._currentSearchResult.find(
          (o) => o.userId === self.currentEditTutorId,
        );
      },
      get foundTutor() {
        return self.oldSearchContextProps.getTutorByEmail(self.foundEmail);
      },
      get resultsPerPage() {
        return 50;
      },
      get displayAllTutors() {
        return true;
      },
      get daysSelected() {
        let schedules = self.splitRequests
          .map((o) => o.lessonDetails.lessonDays)
          .flat();
        let requestSchedule =
          self.requestData.lessonDetails.lessonSchedule.lessonDays || [];
        return Array.from(new Set([...schedules, ...requestSchedule]));
      },
      get isAdminLogin() {
        return true;
      },
      isSelectedTutor(tutorId) {
        return self.oldSearchContextProps.selectedTutorIDs.includes(tutorId);
      },
      get requestInfo() {
        let { slug, serverInfo } = self.useRequestDataProps;
        let { contactDetails, lessonDetails } = self.requestData;
        // return self.useRequestDataProps.jobInfo;
        let totalEarnings = self.splitRequests
          .map((o) => o.rawPaymentInfo)
          .filter(Boolean)
          .map((o) => o?.tutorEarnings);
        let rr = 0;
        if (totalEarnings.length > 0) {
          rr = totalEarnings.reduce((a, b) => a + b, 0);
        }
        return {
          requestSLUG: slug,
          requestStatus: serverInfo?.status,
          dateCreated: serverInfo?.created,
          contactInfo: contactDetails,
          students: serverInfo.tutorRequestInfo
            ? serverInfo.tutorRequestInfo.individualTeacherArray
            : [],
          lessonInfo: {
            ...lessonDetails.lessonSchedule,
            lessonType: lessonDetails.lessonType,
          },
          bookingStage: serverInfo.tutorResponseInfo.bookingStageSummary,
          billingInfo: { ...serverInfo.bookingInfo, tutorEarnings: rr },
        };
      },
      get workingSplitRequest() {
        let existingWithOptions = self.withOptions;
        if (existingWithOptions.length === 0) {
          existingWithOptions = new Array(self.maxNoOfTutors).fill(
            self.splitRequests[0],
          );
        }
        return self.splitRequests.length > 1
          ? self.splitRequests
          : existingWithOptions;
      },
    };
  })
  .actions((self) => {
    const { adapter } = getEnv(self);
    const initializeAdminSearch = flow(function* initializeAdminSearch(
      data?: any,
      currencyForCountry?: string,
    ) {
      self.oldSearchContextProps.updateCurrency(currencyForCountry);
      let payload: any = data;
      if (!payload) {
        payload = yield adapter.initializeAdminSearch();
      }
      yield self.initializeApplication(
        payload.requestInfo,
        "",
        payload.firstSearch,
        payload.tutors,
        payload.specialities,
        0,
        0,
        payload.academicData,
        [],
        true,
      );
      // when tutors have been connected to request. Accounting for their display
      if (self.workingSplitRequest.length > 0 && payload.tutors) {
        if (self.splitRequests.length == 1) {
          // populate withOptions with the splitRequest with tutors
          let tutors = new Array(self.maxNoOfTutors)
            .fill(getSnapshot(self.splitRequests[0]))
            .map((o, i) => {
              let tutor = payload.tutors[i];
              if (tutor) {
                return {
                  ...o,
                  tutorData: tutor,
                };
              }
              return o;
            });
          self.withOptions = cast(tutors);
        } else {
          self.splitRequests.forEach((split, i) => {
            let tutor = payload.tutors[i];
            if (tutor) {
              applySnapshot(split, { ...getSnapshot(split), tutorData: tutor });
            }
          });
        }
      }
      if (payload.requestParameters) {
        self.requestParameters = cast(payload.requestParameters);
      }
      yield self.oldSearchContextProps.onCreate("", payload.firstSearch);
      yield self.oldSearchContextProps.initializeSearchIndex();
      self.useRequestDataProps.updateAdminLogin(true);
      self.useRequestDataProps.updateServerInfo({
        ...payload.serverInfo,
      });
      // If there is a payment information object sent. update the split payment info from the client
      if (self.splitRequests.length > 0 && payload.paymentInfo) {
        self.splitRequests.forEach((split, index) => {
          let { lessonPayments } = payload.paymentInfo;
          applySnapshot(split, {
            ...getSnapshot(split),
            rawPaymentInfo: lessonPayments[index],
          });
        });
      }
      // in the case for a single request, update the client payment information as well
      if (self.withOptions.length > 0 && payload.paymentInfo) {
        self.withOptions.forEach((split, index) => {
          let { lessonPayments } = payload.paymentInfo;
          applySnapshot(split, {
            ...getSnapshot(split),
            rawPaymentInfo: lessonPayments[0],
          });
        });
      }
      const { countries = [], regions = [] } =
        yield adapter.getCountriesAndRegions();

      self.useRequestDataProps.lessonLocationStore.updateFields({
        regions,
        countries,
      });
      self.editTutorInfo.initWithStaticData({
        regions,
        countries,
        countriesSupported: payload.supportedCountries || [],
      });
    });

    const updateRequestParameters = flow(
      function* updateRequestParameters(values) {
        try {
          let result = yield adapter.updateRequestParameters(values);
          self.oldSearchContextProps.initializeSearch(result);
        } catch (error) {
          throw error;
        }
      },
    );
    const findTutor = flow(function* findTutorByEmail(email: string) {
      try {
        self.foundEmail = email;
        // check if tutor already exists in the fetched search result. if not then go to server.
        if (!self.foundTutor) {
          let result = yield adapter.selectDefaultSubject(undefined, email);
          if (result) {
            self.oldSearchContextProps._currentSearchResult.push(result);
          } else {
            throw `Tutor with email ${email} not found `;
          }
        }
      } catch (error) {
        console.log(error);
        throw error;
      }
    });
    const addTutorToPool = flow(function* addTutorToPool(
      defaultSubject: string,
    ) {
      try {
        yield onChangeDefaultSubject(defaultSubject, self.foundTutor.userId);
        self.oldSearchContextProps.onSelectTutor(self.foundTutor.userId);
        self.foundEmail = "";
      } catch (error) {
        throw error;
      }
    });

    const onChangeDefaultSubject = flow(
      function* onChangeDefaultSubject(subject, userId) {
        self.loading = true;
        try {
          const result = yield adapter.selectDefaultSubject(subject, userId);
          self.oldSearchContextProps.updateTutorSubjectInSearchResult(
            userId,
            result,
          );
          self.loading = false;
        } catch (error) {
          self.loading = false;
          throw error;
        }
      },
    );
    const onSendMessage = flow(function* onSendMessage(
      tutorInfo: IExtendedTutorData,
    ) {
      throw new Error("Not Implemented");
    });
    const updateRequestParametersIndex = flow(function* (index) {
      const parameters = yield adapter.createSearchFilter(
        self.requestData,
        index,
      );
      self.requestParameters = cast(parameters);
    });
    return {
      updateRequestParametersIndex,
      onSendMessage,
      findTutor,
      addTutorToPool,
      initializeAdminSearch,
      onChangeDefaultSubject,
      updateSelectedTutors(data, index: number, skip = false) {
        if (self.splitRequests.length > 1) {
          self.useRequestDataProps.updateSelectedTutors(data, index);
        }
        let selectedTutors = self.useRequestDataProps.selectedTutors;
        let splitRequests = self.workingSplitRequest.map((o, i) => {
          // let tt = selectedTutors[i];
          if (i === index) {
            // if (i === index && tt) {
            let condition = skip ? true : data.userId != o.tutorId;

            if (o && condition) {
              return {
                ...getSnapshot(o),
                tutorData: data,
                // tutorData: getSnapshot(data),
                tutorId: data.userId,
              };
            }
          }
          return o ? getSnapshot(o) : o;
        });
        if (self.splitRequests.length > 1) {
          self.splitRequests = cast(splitRequests);
        } else {
          if (!splitRequests.every((u) => u === undefined)) {
            self.withOptions = cast(splitRequests);
          }
        }
        // self.splitRequests = cast(splitRequests);
        // updateSplitRequests();
        // self.splitRequests = cast(
        //   self.splitRequests.map((o, i) => {
        //     if (i === index) {
        //       return {
        //         ...getSnapshot(o),
        //         tutorData: data,
        //         tutorId: data.userId,
        //       };
        //     }
        //     return getSnapshot(o);
        //   })
        // );
        // self.selectedTutors.splice(index, 1, data);
      },
      determineTutorHourlyRate(tutorId: string, totalPrice: number) {
        const { oldSearchContextProps } = self;
        const tutor = oldSearchContextProps.currentSearchResult.find(
          (tutor) => tutor.userId === tutorId,
        );
        const currentIndex =
          self.workingSplitRequest.length > 1
            ? oldSearchContextProps.currentSearchIndex
            : oldSearchContextProps.currentTeacherIndex;
        let currentItem = self.workingSplitRequest[currentIndex];
        const searchObject = self.workingSplitRequest.length
          ? currentItem
            ? getSnapshot(currentItem)
            : undefined
          : undefined;
        if (tutor) {
          return determineTutorHourlyRate(
            {
              totalPrice,
              discount: tutor.subject.discountForExtraStudents,
            },
            self.requestData,
            searchObject,
          );
        }
        return {
          hourlyRate: 0,
          hourlyRatePerStudent: 0,
          numberOfLessons: 0,
          totalPrice,
        };
      },

      updateTutorHourlyRate(tutorId, newRate, additionalInfo) {
        let tutorInstance = self.workingSplitRequest.find(
          (o) => o.tutorId === tutorId,
        );
        if (tutorInstance) {
          tutorInstance.setNewRate(newRate, additionalInfo);
        }
      },
      updateRequestParameters,
      setCurrentEditTutorId(id) {
        const foundTutor = self.oldSearchContextProps.currentSearchResult.find(
          (tutor) => tutor.userId == id,
        );
        if (foundTutor) {
          self.editTutorInfo.updateEditTutorStore(getSnapshot(foundTutor));
        } else {
          self.editTutorInfo.data = {};
        }
        self.currentEditTutorId = id;
      },
    };
  });

export const AdminSearchStore = types
  .compose(ExtendModel, NewSearchStore)
  .named("AdminSearchStore");
// make splitRequests anstpre actual field instead of a view.
